import React, { useState } from "react";
import {
  Box,
  Typography,
  Button,
  useTheme,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
} from "@mui/material";
import LockIcon from "@mui/icons-material/Lock";
import { motion } from "framer-motion";

// Reusable Link Component
const SupportLink = ({ href, children }) => {
  const isExternal = href.startsWith("http");
  return (
    <a
      href={href}
      target={isExternal ? "_blank" : undefined}
      rel="noopener noreferrer"
      style={{
        color: "#000",
        textDecoration: "none",
        fontWeight: 500,
        transition: "all 0.2s ease",
      }}
      onMouseEnter={(e) => {
        e.target.style.color = "#1976d2";
        e.target.style.textDecoration = "underline";
      }}
      onMouseLeave={(e) => {
        e.target.style.color = "#000";
        e.target.style.textDecoration = "none";
      }}
    >
      {children}
    </a>
  );
};

const SubscriptionExpired = () => {
  const theme = useTheme();
  const [open, setOpen] = useState(false);
  // Background Blob Animations
  const blobVariants = {
    animate: {
      y: [0, -40, 0],
      x: [0, 20, 0],
      scale: [1, 1.1, 1],
      transition: { duration: 8, repeat: Infinity, ease: "easeInOut" },
    },
  };

  const blobVariants2 = {
    animate: {
      y: [0, 40, 0],
      x: [0, -30, 0],
      scale: [1, 1.2, 1],
      transition: { duration: 10, repeat: Infinity, ease: "easeInOut", delay: 1 },
    },
  };

  return (
    <>
      <Box
        sx={{
          minHeight: "100vh",
          position: "relative",
          overflow: "hidden",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
        }}
      >
        {/* Animated Background Blobs */}
        <Box
          component={motion.div}
          variants={blobVariants}
          animate="animate"
          sx={{
            position: "fixed",
            width: "50vw",
            height: "50vw",
            borderRadius: "50%",
            background: `radial-gradient(circle, ${theme.palette.primary.light} 0%, rgba(255,255,255,0) 70%)`,
            opacity: 0.4,
            filter: "blur(40px)",
            willChange: "transform",
            zIndex: 0,
          }}
        />
        <Box
          component={motion.div}
          variants={blobVariants2}
          animate="animate"
          sx={{
            position: "absolute",
            bottom: "-10%",
            right: "-5%",
            width: "45vw",
            height: "45vw",
            borderRadius: "50%",
            background: `radial-gradient(circle, ${theme.palette.secondary.light} 0%, rgba(255,255,255,0) 70%)`,
            opacity: 0.3,
            filter: "blur(40px)",
            willChange: "transform",
            zIndex: 0,
          }}
        />

        {/* Glass Card */}
        <Box
          component={motion.div}
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
          sx={{
            position: "relative",
            zIndex: 1,
            width: "100%",
            maxWidth: "500px",
            background: "rgba(255, 255, 255, 0.65)",
            backdropFilter: "blur(20px)",
            borderRadius: 4,
            p: 6,
            textAlign: "center",
            boxShadow: "0 8px 32px 0 rgba(31, 38, 135, 0.15)",
            border: "1px solid rgba(255, 255, 255, 0.18)",
            mx: 2,
          }}
        >
          <motion.div
                    animate={{ y: [0, -15, 0] }}
                    transition={{
                        duration: 3,
                        repeat: Infinity,
                        ease: "easeInOut",
                    }}
                >
                    <LockIcon sx={{ fontSize: 100, color: 'primary.main', mb: 3 }} />
                </motion.div>
          
          {/* Gradient Header */}
          <Typography
            variant="h2"
            sx={{
              background: `linear-gradient(45deg, ${theme.palette.primary.main}, ${theme.palette.primary.dark})`,
              WebkitBackgroundClip: "text",
              WebkitTextFillColor: "transparent",
              mb: 1,
              fontWeight: 700,
            }}
          >
            Oops!
          </Typography>

          <Typography variant="h4" fontWeight={600} mt={1}>
            Subscription Expired
          </Typography>

          <Typography mt={2} color="text.secondary" sx={{ lineHeight: 1.6 }}>
            Your subscription has expired. <br />
            Please contact the Support Team for assistance.
          </Typography>

          <Button
            variant="contained"
            sx={{
              mt: 4,
              px: 4,
              py: 1.5,
              borderRadius: 2,
              textTransform: "none",
              transition: "transform 0.2s",
              "&:hover": { transform: "scale(1.05)" },
            }}
            onClick={() => setOpen(true)}
          >
            Contact Support
          </Button>
        </Box>
      </Box>

      {/* Support Dialog */}
      <Dialog
        open={open}
        onClose={() => setOpen(false)}
        aria-labelledby="contact-support-title"
        PaperProps={{ sx: { borderRadius: 3, p: 2, width: 380 } }}
      >
        <DialogTitle
          id="contact-support-title"
          sx={{
            fontWeight: 600,
            color: "#fff",
            backgroundColor: "#ea8367",
            borderTopLeftRadius: 12,
            borderTopRightRadius: 12,
            textAlign: "center",
          }}
        >
          Contact Support
        </DialogTitle>

        <DialogContent sx={{ mt: 2 }}>
          {/* Website */}
          <Typography variant="body2" color="text.secondary">
            Website
          </Typography>
          <Typography sx={{ mt: 0.5 }}>
            <SupportLink href="https://belageri.com">https://belageri.com</SupportLink>
          </Typography>

          {/* Email */}
          <Typography variant="body2" color="text.secondary" sx={{ mt: 3 }}>
            Email
          </Typography>
          <Typography sx={{ mt: 0.5 }}>
            <SupportLink href="mailto:contactus@belageri.com">contactus@belageri.com</SupportLink>
          </Typography>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button
            onClick={() => setOpen(false)}
            variant="contained"
            sx={{
              borderRadius: 2,
              textTransform: "none",
              px: 3,
            }}
            autoFocus
          >
            Close
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default SubscriptionExpired;